// The compose lifecycle — architecture/MAIL.md §3.2 + §3.2a.
//
// ONE draft slot. `root.mailComposeDraft` is the composer's working copy; the
// server `MailDraft` row (created by the 2 s autosave) is its durable twin,
// and `sessionStorage` mirrors the slot per workspace so the draft survives a
// same-tab OAuth round trip and an accidental reload (§3.2a "a draft started
// before setup is never lost"). There is no server draft LIST route (§5.7 —
// "the composer holds its draft"), so the slot + the mirror ARE the recovery
// path until provider draft mirroring lands (P2).
//
// Entry is exactly one function, `openMailCompose(draft?)` (§3.2a): the
// sidebar Compose, the drawer Compose, the person card Email action, the
// mailto: interception, the assistant tool and the `c` shortcut all call it,
// and the SERVER's setup state decides what opens — the dock only in `ready`,
// MailSetupPanel everywhere else. This file rides functions/eagerSlice.js
// because five of those six callers fire on routes where the mail context has
// not folded yet.
//
// Body html lives in the contenteditable DOM while the person types (the
// ChatComposer idiom — state-invisible content), and is flushed into the
// draft on the autosave tick, on close, and before send. The editor node
// registers itself here (registerMailComposerEditor) the way chat's
// registerComposerEditor does.
import { getSDK } from '@symbo-ls/workspace-shared/functions/sdk.js'
import { mailSameWorkspaceWriter, mailWorkspaceId } from './sameWorkspaceWriter.js'
import { readParkedMailDraft, writeParkedMailDraft } from './composeSession.js'
import { enqueueMailComposeWrite } from './composeChain.js'

// §3.2: "Drafts autosave locally every 2 s (debounced) to MailDraft".
const MAIL_AUTOSAVE_MS = 2000

// ── module state (bundler-scoped, same class as chat's activeMentionEditor) ──
// The live contenteditable node, one composer at a time.
let _mailEditorNode = null
// The autosave debounce. The saves themselves SERIALISE on the compose write
// queue (./composeChain.js) so a create can never race a second create, and
// no keep-list update can interleave an attachment upload.
let _autosaveTimer = null

export const registerMailComposerEditor = function (node) {
  _mailEditorNode = node || null
}

export const unregisterMailComposerEditor = function (node) {
  if (_mailEditorNode === node) _mailEditorNode = null
}

// Live MailComposeDock mounts (the dock registers on create, like the editor
// above). `openMailCompose` navigates to /mail ONLY when no dock is mounted:
// forcing `openAppPage('/mail', { preferSlot })` while the full-page /mail
// route is up opened a SECOND /mail inside the PageSlot — two docks over one
// draft (measured live, headless probe 2026-09-05). A mounted-but-hidden
// dock counts: `mailComposeOpen` unhides it right where it is.
let _mailDockCount = 0

export const registerMailComposeDock = function () {
  _mailDockCount += 1
}

export const unregisterMailComposeDock = function () {
  _mailDockCount = Math.max(0, _mailDockCount - 1)
}

// ── pure helpers (exported for tests and for the entry surfaces) ────────────

// One address entry. Accepts 'a@b.c', { email }, { name, email },
// { address, name } (the server sendAs shape) — answers { name, email } or
// null when there is no usable address.
export const mailAddress = function (input) {
  if (!input) return null
  if (typeof input === 'string') {
    const email = input.trim()
    return email ? { name: '', email } : null
  }
  const email = String(input.email || input.address || '').trim()
  if (!email) return null
  return { name: String(input.name || ''), email }
}

export const mailAddressList = function (input) {
  const arr = Array.isArray(input) ? input : input ? [input] : []
  const out = []
  for (const entry of arr) {
    const a = mailAddress(entry)
    if (a && !out.some((x) => x.email.toLowerCase() === a.email.toLowerCase())) out.push(a)
  }
  return out
}

export const isMailAddress = function (text) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(String(text || '').trim())
}

// The composer's working shape. Every key is declared so root.update never
// meets an undeclared nested read, and `merge` layers an intent (a person
// card's `to`, a mailto's subject) over it.
export const normalizeMailDraft = function (input) {
  const d = input && typeof input === 'object' ? input : {}
  const html =
    typeof d.html === 'string' && d.html
      ? d.html
      : typeof d.body === 'string' && d.body
        ? mailTextToHtml(d.body)
        : ''
  return {
    id: d.id || null,
    accountId: d.accountId || d.account || null,
    sendAs: d.sendAs || null,
    to: mailAddressList(d.to),
    cc: mailAddressList(d.cc),
    bcc: mailAddressList(d.bcc),
    subject: typeof d.subject === 'string' ? d.subject : '',
    html,
    attachments: Array.isArray(d.attachments) ? d.attachments : [],
    inReplyToMessage: d.inReplyToMessage || null,
    thread: d.thread || null,
    seeded: !!d.seeded
  }
}

// Plain text → the html the composer edits: escape, then newlines to <br>.
export const mailTextToHtml = function (text) {
  return String(text || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/\r\n|\r|\n/g, '<br>')
}

// mailto:a@b.c,d@e.f?subject=&cc=&bcc=&body= → a draft intent, or null when
// the href is not a mailto. RFC 6068 keys are case-insensitive.
export const parseMailtoHref = function (href) {
  const raw = String(href || '')
  if (!/^mailto:/i.test(raw)) return null
  const rest = raw.slice(7)
  const qIdx = rest.indexOf('?')
  const addrPart = qIdx === -1 ? rest : rest.slice(0, qIdx)
  const draft = { to: [], cc: [], bcc: [], subject: '', html: '' }
  const dec = (v) => {
    try {
      return decodeURIComponent(v.replace(/\+/g, '%20'))
    } catch (e) {
      return v
    }
  }
  if (addrPart) {
    draft.to = addrPart
      .split(',')
      .map((a) => dec(a).trim())
      .filter(Boolean)
  }
  if (qIdx !== -1) {
    for (const pair of rest.slice(qIdx + 1).split('&')) {
      if (!pair) continue
      const eq = pair.indexOf('=')
      const key = dec(eq === -1 ? pair : pair.slice(0, eq)).toLowerCase()
      const value = eq === -1 ? '' : dec(pair.slice(eq + 1))
      if (key === 'to') draft.to = draft.to.concat(value.split(',').map((a) => a.trim()))
      else if (key === 'cc') draft.cc = value.split(',').map((a) => a.trim())
      else if (key === 'bcc') draft.bcc = value.split(',').map((a) => a.trim())
      else if (key === 'subject') draft.subject = value
      else if (key === 'body') draft.html = mailTextToHtml(value)
    }
  }
  return normalizeMailDraft(draft)
}

// Reply / reply-all / forward → a draft intent. PURE: the caller resolves the
// localized attribution strings (quoteIntro carries {date} and {who}) and the
// loaded body html; `selfAddresses` drops the viewer's own addresses from a
// reply-all so people never reply to themselves.
export const buildMailReplyDraft = function (message, opts) {
  const m = message || {}
  const o = opts || {}
  const mode = o.mode === 'replyAll' || o.mode === 'forward' ? o.mode : 'reply'
  const self = (o.selfAddresses || []).map((a) => String(a).toLowerCase())
  const stripPrefixes = (s) => String(s || '').replace(/^\s*((re|fwd?|fw)\s*:\s*)+/i, '')
  const from = mailAddress(m.from)
  const replyTo = mailAddressList(m.replyTo && m.replyTo.length ? m.replyTo : from ? [from] : [])
  const notSelf = (a) => !self.includes(a.email.toLowerCase())
  const bodyHtml = String(o.bodyHtml || '')
  const who = from ? (from.name ? `${from.name} <${from.email}>` : from.email) : ''
  const esc = (s) => String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  const draft = {
    accountId: m.account || null,
    to: [],
    cc: [],
    subject: (mode === 'forward' ? 'Fwd: ' : 'Re: ') + stripPrefixes(m.subject),
    html: '',
    inReplyToMessage: mode === 'forward' ? null : m.id || null,
    thread: mode === 'forward' ? null : m.thread || null
  }
  if (mode === 'reply') {
    draft.to = replyTo
  } else if (mode === 'replyAll') {
    draft.to = mailAddressList([...replyTo, ...mailAddressList(m.to).filter(notSelf)])
    draft.cc = mailAddressList(m.cc).filter(notSelf)
  }
  if (mode === 'forward') {
    const line = (label, value) => (value ? `${esc(label)}: ${esc(value)}<br>` : '')
    const toLine = mailAddressList(m.to)
      .map((a) => (a.name ? `${a.name} <${a.email}>` : a.email))
      .join(', ')
    draft.html =
      '<br><br>' +
      `<div>${esc(o.forwardIntro || '---------- Forwarded message ----------')}</div>` +
      line(o.fromLabel || 'From', who) +
      line(o.dateLabel || 'Date', m.sentAt || m.receivedAt || '') +
      line(o.subjectLabel || 'Subject', m.subject || '') +
      line(o.toLabel || 'To', toLine) +
      '<br>' +
      bodyHtml
  } else if (bodyHtml || who) {
    const intro = String(o.quoteIntro || 'On {date}, {who} wrote:')
      .replace('{date}', esc(m.sentAt || m.receivedAt || ''))
      .replace('{who}', esc(who))
    draft.html = `<br><br><div>${intro}</div><blockquote type="cite">${bodyHtml}</blockquote>`
  }
  return normalizeMailDraft(draft)
}

// New-message seeding: the per-account signature (§3.2 "signature per
// account"), appended once. A resumed draft (server id) or an already-seeded
// slot is left alone; a signature-less account seeds nothing.
export const composeSeedHtml = function (draft, account) {
  const d = draft || {}
  if (d.seeded || d.id) return d.html || ''
  const sig =
    (account && account.signature && account.signature.html) ||
    (account && account.signature && account.signature.text
      ? mailTextToHtml(account.signature.text)
      : '')
  if (!sig) return d.html || ''
  return `${d.html || ''}<br><br><div class="mail-signature">${sig}</div>`
}

// To/Cc/Bcc autocomplete rows — root.people (the shell keeps name/email/
// avatar/initials in sync, functions/loadPrivateData.js `_person`), minus the
// addresses already chipped. Same match the reminder mention picker uses:
// name or the email local part.
export const mailRecipientMatches = function (root, query, excludeEmails) {
  const rt = root || (this && this.state && this.state.root)
  const q = String(
    query == null ? (rt && rt.mailRecipientPicker && rt.mailRecipientPicker.query) || '' : query
  )
    .toLowerCase()
    .trim()
  if (!rt || !q) return []
  const taken = (excludeEmails || []).map((e) => String(e).toLowerCase())
  return ((rt && rt.people) || [])
    .filter((p) => p && p.email && !taken.includes(p.email.toLowerCase()))
    .filter((p) => {
      const name = (p.name || '').toLowerCase()
      const local = (p.email || '').split('@')[0].toLowerCase()
      return name.includes(q) || local.includes(q) || p.email.toLowerCase().includes(q)
    })
    .slice(0, 8)
}

// The send-capable account for a fresh draft: the draft's own account when it
// still can send, else the account marked default by the gate order (the
// §3.2a summaries are ACL-ordered), else null. `canSend` + `active` is the
// same pair the server's `ready` state is made of.
export const pickMailComposeAccount = function (root, draft) {
  const rt = root || (this && this.state && this.state.root)
  const accounts = (rt && rt.mailSetup && rt.mailSetup.accounts) || []
  const sendable = accounts.filter((a) => a && a.canSend && a.status === 'active')
  const wanted = draft && (draft.accountId || draft.account)
  if (wanted && sendable.some((a) => a.id === wanted)) return wanted
  return (sendable[0] && sendable[0].id) || null
}

// ── the draft slot ──────────────────────────────────────────────────────────

// Flush the live editor html into the slot. Direct root.update (no autosave
// re-schedule): flushing is what the autosave itself calls first.
export const flushMailComposeHtml = function (root) {
  const rt = root || (this && this.state && this.state.root)
  const draft = rt && rt.mailComposeDraft
  if (!rt || !draft || !_mailEditorNode) return
  const html = _mailEditorNode.innerHTML || ''
  if (html === draft.html) return
  rt.update({ mailComposeDraft: { ...draft, html } })
}

// Patch the slot + mirror it + arm the 2 s autosave. Every composer edit
// (chips, subject, account, editor input) lands here. An EMPTY patch arms
// the timer only — that is the editor's keystroke path, which must never
// re-render the dock per key.
export const mailComposePatch = function (patch) {
  const root = this && this.state && this.state.root
  if (!root || !root.mailComposeDraft) return
  if (patch && Object.keys(patch).length) {
    const draft = { ...root.mailComposeDraft, ...patch }
    root.update({ mailComposeDraft: draft })
    writeParkedMailDraft(mailWorkspaceId(root), draft)
  }
  if (_autosaveTimer) clearTimeout(_autosaveTimer)
  _autosaveTimer = setTimeout(() => {
    _autosaveTimer = null
    saveMailDraft({ root })
  }, MAIL_AUTOSAVE_MS)
}

// The editor's per-keystroke hook: content stays in the DOM (never a
// root.update per keystroke) — this only arms the 2 s autosave, whose flush
// reads `editor.innerHTML`.
export const mailComposeTouch = function () {
  this.call('mailComposePatch', {})
}

export const mailComposeSetAccount = function (accountId) {
  // Switching From resets the alias — the old alias belongs to the old
  // account and the server would 400 invalid_send_as on it.
  this.call('mailComposePatch', { accountId: accountId || null, sendAs: null })
}

// The 2 s tick, close, and send all funnel here. Saves SERIALISE on one
// chain, so a slow create can never overlap the next tick's update and mint
// a second server draft. The chain settles even on failure (errors land in
// `mailComposeError`, never throw).
export const saveMailDraft = function (opts) {
  const root = (opts && opts.root) || (this && this.state && this.state.root)
  if (!root) return Promise.resolve(null)
  return enqueueMailComposeWrite(async () => {
    flushMailComposeHtml(root)
    const draft = root.mailComposeDraft
    if (!draft) return null
    const accountId = draft.accountId || pickMailComposeAccount(root, draft)
    if (!accountId) return null
    if (!draft.id && !mailDraftHasContent(draft)) return null
    const write = mailSameWorkspaceWriter(root)
    const workspaceId = mailWorkspaceId(root)
    root.update({ mailComposeSaving: true })
    const payload = {
      account: accountId,
      to: draft.to,
      cc: draft.cc,
      bcc: draft.bcc,
      subject: draft.subject,
      html: draft.html,
      ...(draft.inReplyToMessage ? { inReplyToMessage: draft.inReplyToMessage } : {}),
      ...(draft.thread ? { thread: draft.thread } : {})
    }
    try {
      let saved
      if (!draft.id) {
        saved = await getSDK().execute('mail.drafts', 'create', {
          payload,
          ...(workspaceId ? { workspaceId } : {})
        })
      } else {
        saved = await getSDK().execute('mail.drafts', 'update', {
          id: draft.id,
          payload: {
            ...payload,
            // The keep-list contract: echo the staged refs still on the
            // draft; a ref left out is dropped server-side with its blob.
            attachments: draft.attachments.map((a) => a && a.storagePath).filter(Boolean)
          },
          ...(workspaceId ? { workspaceId } : {})
        })
      }
      // Merge ONLY the server-minted facts into the CURRENT slot — the person
      // kept typing during the await, so `saved`'s content is already stale.
      const current = root.mailComposeDraft
      if (current) {
        const next = {
          ...current,
          id: (saved && saved.id) || current.id,
          accountId: current.accountId || accountId
        }
        if (
          write({
            mailComposeDraft: next,
            mailComposeSaving: false,
            mailComposeSavedAt: Date.now()
          })
        ) {
          writeParkedMailDraft(workspaceId, next)
        }
      } else {
        write({ mailComposeSaving: false, mailComposeSavedAt: Date.now() })
      }
      return saved || null
    } catch (err) {
      write({
        mailComposeSaving: false,
        mailComposeError: {
          op: 'save',
          code: (err && err.status) || null,
          message: (err && err.message) || ''
        }
      })
      return null
    }
  })
}

const mailHtmlHasContent = (html) =>
  !!String(html || '')
    .replace(/<br\s*\/?>/gi, '')
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/gi, '')
    .trim()

// Anything a person typed or attached — the autosave's "worth a server row"
// test, and the discard's "worth asking about" test.
const mailDraftHasContent = (draft) =>
  !!(
    draft.subject ||
    (draft.to || []).length ||
    (draft.cc || []).length ||
    (draft.bcc || []).length ||
    (draft.attachments || []).length ||
    mailHtmlHasContent(draft.html)
  )

// ── the §3.2a gate — THE compose entry point ────────────────────────────────
// Every caller — sidebar Compose, the drawer button, a person card's Email
// action, the mailto interception, the assistant tool, the `c` shortcut —
// calls this ONE function, and the SERVER's setup state decides what happens:
// the dock opens only in `ready`; every other state opens the drawer, where
// MailSetupPanel teaches the setup instead of failing.
export const openMailCompose = async function (draft) {
  const root = this && this.state && this.state.root
  if (!root) return null
  // Captured on THIS tick, before the gate read and before the fold — both
  // are awaits, and both of the writes below land after them. A compose
  // intent formed in workspace A must not open A's dock, or A's drawer, over
  // workspace B (see functions/sameWorkspaceWriter.js).
  const write = mailSameWorkspaceWriter(root)
  const workspaceId = mailWorkspaceId(root)
  const intent = draft ? normalizeMailDraft(draft) : null
  // Park the intent BEFORE the gate read: §3.2a — the draft must ride across
  // the whole setup journey (OAuth, link, ask-admin) untouched.
  if (intent) {
    root.update({ mailComposeDraft: intent })
    writeParkedMailDraft(workspaceId, intent)
  }
  const setup = await this.call('loadMailSetup')
  const state = (setup && setup.state) || (root.mailSetup && root.mailSetup.state) || null
  if (state === 'off') return state
  if (state === 'ready') {
    // Resume order: the slot → the session mirror (a same-tab OAuth return
    // or a reload emptied the slot but not the mirror) → a fresh draft.
    let prepared = root.mailComposeDraft
    if (!prepared) prepared = readParkedMailDraft(workspaceId)
    prepared = normalizeMailDraft(prepared || {})
    if (!prepared.accountId) prepared.accountId = pickMailComposeAccount(root, prepared)
    // The full account rows carry sendAs + signature + undo seconds; the
    // gate summaries do not. Fire-and-forget — the dock renders from the
    // summaries meanwhile.
    if (!(root.mailAccounts || []).length) this.call('loadMailAccounts')
    if (
      !write({
        _mailDrawerOpen: false,
        mailComposeOpen: true,
        mailComposeDraft: prepared,
        mailComposeError: null
      })
    ) {
      return state
    }
    writeParkedMailDraft(workspaceId, prepared)
    if (!_mailDockCount) this.call('openAppPage', '/mail', { preferSlot: true })
    return state
  }
  const ok = await this.call('ensureMailContext')
  if (ok === false) return state
  write({ _mailDrawerOpen: true })
  return state
}

// mailto: interception (§3.2a) — the shell's document-level click handler
// calls this with the anchor's href when the mail flag is on. Answers false
// for a non-mailto href so the caller lets the browser have it.
export const openMailComposeFromMailto = function (href) {
  const draft = parseMailtoHref(href)
  if (!draft) return false
  this.call('openMailCompose', draft)
  return true
}

// The assistant surface (spec §3.2a "the assistant tool mail_draft_reply").
// Client half only: the server-side tool definition + the approval-gated
// send land with MAIL-AI-TOOLS-1 — this makes the dispatch call the same
// one function every other surface calls.
export const aiMailDraftReply = async function (to, subject, body) {
  const state = await this.call('openMailCompose', {
    to,
    subject: subject || '',
    body: body || ''
  })
  return { ok: state === 'ready', setupState: state || 'unknown' }
}

// Close = PARK, not discard: the slot and the mirror keep the draft (the
// autosave already put it on the server when it had content), and the next
// openMailCompose resumes it.
export const closeMailCompose = function () {
  const root = this && this.state && this.state.root
  if (!root) return
  flushMailComposeHtml(root)
  saveMailDraft({ root })
  root.update({ mailComposeOpen: false, mailRecipientPicker: null, _mailComposeMenu: null })
}

// [Discard] asks first (CRUD + confirmation standard, rule 3): the server
// deletes the draft row with its staged attachments (MailSendService
// deleteDraft), and drafts live only here, so nothing brings it back. A draft
// that holds nothing and was never saved has nothing to lose and closes at
// once. The draft is read again when the person confirms: an autosave that
// lands while the dialog is open has minted the server id that must go too.
export const discardMailCompose = function () {
  const root = this && this.state && this.state.root
  if (!root) return Promise.resolve(false)
  flushMailComposeHtml(root)
  const draft = root.mailComposeDraft
  if (!draft || (!draft.id && !mailDraftHasContent(draft))) {
    return _discardMailComposeNow.call(this, root).then(() => true)
  }
  const subject = String(draft.subject || '').trim()
  return new Promise((resolve) => {
    this.call('confirmDialog', {
      title: subject
        ? this.call('polyglot', 'mail.confirm.discard.title').replace('{subject}', () => subject)
        : this.call('polyglot', 'mail.confirm.discard.titleUntitled'),
      message: this.call('polyglot', 'mail.confirm.discard.message'),
      confirmText: this.call('polyglot', 'mail.confirm.discard.button'),
      cancelText: this.call('polyglot', 'common.action.cancel'),
      confirmTheme: 'primary',
      icon: null,
      onConfirm: () => resolve(_discardMailComposeNow.call(this, root).then(() => true)),
      onCancel: () => resolve(false)
    })
  })
}

const _discardMailComposeNow = async function (root) {
  const draft = root.mailComposeDraft
  const write = mailSameWorkspaceWriter(root)
  const workspaceId = mailWorkspaceId(root)
  if (_autosaveTimer) {
    clearTimeout(_autosaveTimer)
    _autosaveTimer = null
  }
  root.update({
    mailComposeOpen: false,
    mailComposeDraft: null,
    mailComposeError: null,
    mailRecipientPicker: null,
    _mailComposeMenu: null
  })
  writeParkedMailDraft(workspaceId, null)
  if (draft && draft.id) {
    try {
      await getSDK().execute('mail.drafts', 'remove', {
        id: draft.id,
        ...(workspaceId ? { workspaceId } : {})
      })
    } catch (err) {
      // The staged blobs outlive the slot; the server's own draft sweep
      // reaps them. Nothing the person can act on — keep the close silent.
      write({})
    }
  }
}

// ── send / undo / schedule (§3.2 "Sending", §5.7) ──────────────────────────

export const sendMailCompose = async function (opts) {
  const root = this && this.state && this.state.root
  if (!root || root.mailComposeBusy) return false
  const scheduleAt = opts && opts.scheduleAt ? opts.scheduleAt : null
  const write = mailSameWorkspaceWriter(root)
  const workspaceId = mailWorkspaceId(root)
  const before = root.mailComposeDraft
  if (!before) return false
  if (!(before.to.length + before.cc.length + before.bcc.length)) {
    root.update({ mailComposeError: { op: 'send', code: 'no_recipients', message: '' } })
    return false
  }
  root.update({ mailComposeBusy: 'send', mailComposeError: null, _mailComposeMenu: null })
  // The final flush + save gives the outbox row the exact content on screen.
  await saveMailDraft({ root })
  const draft = root.mailComposeDraft
  if (!draft || !draft.id) {
    write({
      mailComposeBusy: null,
      mailComposeError: root.mailComposeError || { op: 'send', code: 'save_failed', message: '' }
    })
    return false
  }
  try {
    const row = await getSDK().execute('mail.outbox', 'send', {
      payload: {
        draftId: draft.id,
        accountId: draft.accountId,
        ...(draft.sendAs ? { sendAs: draft.sendAs } : {}),
        // undoSeconds is OMITTED on purpose: the server reads the account's
        // own settings.undoSeconds (default 10) — §3.4's per-account dial.
        ...(scheduleAt ? { scheduleAt } : {})
      },
      ...(workspaceId ? { workspaceId } : {})
    })
    if (
      write({
        mailComposeOpen: false,
        mailComposeDraft: null,
        mailComposeBusy: null,
        mailRecipientPicker: null,
        mailOutbox: [row, ...(root.mailOutbox || [])].slice(0, 20)
      })
    ) {
      writeParkedMailDraft(workspaceId, null)
    }
    if (scheduleAt) {
      this.call('openNotification', {
        type: 'success',
        title: this.call('polyglot', 'mail.compose.scheduled'),
        message: new Date(scheduleAt).toLocaleString()
      })
    } else {
      // The undo affordance (§3.2 "Undo send window"): the toast IS the
      // control — a click cancels the queued row and reopens the draft.
      this.call('openNotification', {
        type: 'info',
        icon: 'email',
        title: this.call('polyglot', 'mail.compose.sending'),
        message: this.call('polyglot', 'mail.compose.undoHint'),
        action: { call: 'undoMailSend', args: [row && row.id] }
      })
    }
    return true
  } catch (err) {
    // §3.2: "failure → toast + the draft reopens with the error." The dock
    // never closed, so the reopen is the error landing beside the draft.
    write({
      mailComposeBusy: null,
      mailComposeError: {
        op: 'send',
        code: (err && err.cause && err.cause.code) || (err && err.status) || null,
        message: (err && err.message) || ''
      }
    })
    this.call('openNotification', {
      type: 'error',
      title: this.call('polyglot', 'mail.compose.sendFailed'),
      message: (err && err.message) || ''
    })
    return false
  }
}

// The undo: cancel the queued outbox row; the server restores the draft and
// the dock reopens with it (same slot, same id — the autosave continues).
export const undoMailSend = async function (outboxId) {
  const root = this && this.state && this.state.root
  if (!root || !outboxId) return false
  const write = mailSameWorkspaceWriter(root)
  const workspaceId = mailWorkspaceId(root)
  try {
    const res = await getSDK().execute('mail.outbox', 'cancel', {
      id: outboxId,
      ...(workspaceId ? { workspaceId } : {})
    })
    const restored = res && res.draft ? normalizeMailDraft({ ...res.draft, seeded: true }) : null
    const rows = (root.mailOutbox || []).map((r) =>
      r && r.id === outboxId ? { ...r, status: 'cancelled' } : r
    )
    if (
      write({
        mailOutbox: rows,
        ...(restored ? { mailComposeDraft: restored, mailComposeOpen: true } : {})
      })
    ) {
      if (restored) writeParkedMailDraft(workspaceId, restored)
    }
    if (restored && !_mailDockCount) this.call('openAppPage', '/mail', { preferSlot: true })
    return true
  } catch (err) {
    // 409 not_cancellable — the worker already claimed the row. Late, not
    // broken: say it went out.
    this.call('openNotification', {
      type: err && err.status === 409 ? 'info' : 'error',
      title: this.call(
        'polyglot',
        err && err.status === 409 ? 'mail.compose.undoLate' : 'mail.compose.undoFailed'
      ),
      message: err && err.status === 409 ? '' : (err && err.message) || ''
    })
    return false
  }
}

// ── reply / reply-all / forward (§3.2 entry: the thread view's actions) ────
// The thread view (MAIL-UI-READ-1) calls these with the message envelope; the
// quoted body comes from the loaded body cache when the message is open.
export const replyToMailMessage = async function (message, opts) {
  const root = this && this.state && this.state.root
  if (!root || !message) return null
  const mode = (opts && opts.mode) || 'reply'
  const account =
    (root.mailAccounts || []).find((a) => a && a.id === message.account) ||
    ((root.mailSetup && root.mailSetup.accounts) || []).find((a) => a && a.id === message.account)
  const selfAddresses = [
    account && account.address,
    ...(((account && account.sendAs) || []).map((s) => s && s.address) || [])
  ].filter(Boolean)
  const body = root.mailBodies && message.id ? root.mailBodies[message.id] : null
  const draft = buildMailReplyDraft(message, {
    mode,
    selfAddresses,
    bodyHtml: (body && body.html) || '',
    quoteIntro: this.call('polyglot', 'mail.compose.quoteIntro'),
    forwardIntro: this.call('polyglot', 'mail.compose.fwdIntro')
  })
  return this.call('openMailCompose', draft)
}

export const replyAllToMailMessage = function (message) {
  return this.call('replyToMailMessage', message, { mode: 'replyAll' })
}

export const forwardMailMessage = function (message) {
  return this.call('replyToMailMessage', message, { mode: 'forward' })
}

// ── recipient chips ────────────────────────────────────────────────────────

export const setMailRecipientQuery = function (field, query) {
  const root = this && this.state && this.state.root
  if (!root) return
  if (query == null || query === '') {
    if (root.mailRecipientPicker) root.update({ mailRecipientPicker: null })
    return
  }
  root.update({ mailRecipientPicker: { field, query: String(query) } })
}

// Commit a chip: a picked person ({ name, email }) or typed text. Typed text
// must LOOK like an address; anything else stays in the input.
export const addMailRecipient = function (field, entry) {
  const root = this && this.state && this.state.root
  const draft = root && root.mailComposeDraft
  if (!root || !draft) return false
  const a = mailAddress(entry)
  if (!a || !isMailAddress(a.email)) return false
  const key = field === 'cc' || field === 'bcc' ? field : 'to'
  if (draft[key].some((x) => x.email.toLowerCase() === a.email.toLowerCase())) {
    root.update({ mailRecipientPicker: null })
    return true
  }
  this.call('mailComposePatch', { [key]: [...draft[key], a] })
  root.update({ mailRecipientPicker: null })
  return true
}

export const removeMailRecipient = function (field, email) {
  const root = this && this.state && this.state.root
  const draft = root && root.mailComposeDraft
  if (!root || !draft) return
  const key = field === 'cc' || field === 'bcc' ? field : 'to'
  const target = String(email || '').toLowerCase()
  this.call('mailComposePatch', {
    [key]: draft[key].filter((x) => x.email.toLowerCase() !== target)
  })
}
